import React, { useState, useEffect, useMemo } from 'react';
import { GraduationCap, Save, AlertCircle, Info, Building2, Globe, Pencil, Check } from 'lucide-react';
import { calendarSettingsService, CreateAcademicYearTemplateDto } from '../../services/calendarSettingsService';
import { fetchCampuses, Campus } from '../../../../services/etablissementService';

const MONTHS = [
  { value: 1, label: 'Janvier' },
  { value: 2, label: 'Février' },
  { value: 3, label: 'Mars' },
  { value: 4, label: 'Avril' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Juin' },
  { value: 7, label: 'Juillet' },
  { value: 8, label: 'Août' },
  { value: 9, label: 'Septembre' },
  { value: 10, label: 'Octobre' },
  { value: 11, label: 'Novembre' },
  { value: 12, label: 'Décembre' },
];

const DAYS_PER_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// Helper to determine if a date is in the "next year" relative to academic year start
const isNextYear = (month: number, startMonth: number): boolean => {
  return month < startMonth;
};

// Get month label with optional "année suivante" indicator
const getMonthLabel = (month: number, startMonth: number, showNextYear = true): string => {
  const monthObj = MONTHS.find(m => m.value === month);
  const label = monthObj?.label || '';
  if (showNextYear && isNextYear(month, startMonth)) {
    return `${label} (année suivante)`;
  }
  return label;
};

// Format a date for display with next year indicator
const formatDateDisplay = (day: number, month: number, startMonth: number): string => {
  const monthObj = MONTHS.find(m => m.value === month);
  const monthLabel = monthObj?.label || '';
  const nextYearIndicator = isNextYear(month, startMonth) ? ' (année suivante)' : '';
  return `${day} ${monthLabel}${nextYearIndicator}`;
};

// Calculate the end date based on start date (one day before start date, next year)
const calculateEndDate = (startDay: number, startMonth: number): { endDay: number; endMonth: number } => {
  let endDay = startDay - 1;
  let endMonth = startMonth;

  if (endDay === 0) {
    endMonth = startMonth - 1;
    if (endMonth === 0) {
      endMonth = 12;
    }
    endDay = DAYS_PER_MONTH[endMonth - 1];
  }

  return { endDay, endMonth };
};

// Compare two dates within academic year context
// Returns: negative if date1 < date2, positive if date1 > date2, 0 if equal
// isDate2EndOfYear: set to true when date2 is the academic year end date (which is always in the "next year")
const compareDatesInAcademicYear = (
  day1: number,
  month1: number,
  day2: number,
  month2: number,
  academicStartMonth: number,
  isDate2EndOfYear: boolean = false
): number => {
  // Normalize months: months >= startMonth stay as-is, months < startMonth get +12 (next calendar year)
  const normalizeMonth = (m: number) => (m >= academicStartMonth ? m : m + 12);

  const val1 = normalizeMonth(month1) * 100 + day1;

  // For academic year end date: if the end month equals start month, it's in the NEXT year
  // So we need to add 12 to treat it as after the full academic year
  let val2: number;
  if (isDate2EndOfYear && month2 === academicStartMonth) {
    // End date is in the same month as start but next year - add 12
    val2 = (month2 + 12) * 100 + day2;
  } else {
    val2 = normalizeMonth(month2) * 100 + day2;
  }

  return val1 - val2;
};

// Validate form data and return array of error messages
const validateFormData = (formData: CreateAcademicYearTemplateDto): string[] => {
  const errors: string[] = [];

  const startDay = formData.startDay || 1;
  const startMonth = formData.startMonth || 9;
  const { endDay, endMonth } = calculateEndDate(startDay, startMonth);

  const sem1StartDay = formData.semester1StartDay || 1;
  const sem1StartMonth = formData.semester1StartMonth || 9;
  const sem1EndDay = formData.semester1EndDay || 31;
  const sem1EndMonth = formData.semester1EndMonth || 1;
  const sem2StartDay = formData.semester2StartDay || 1;
  const sem2StartMonth = formData.semester2StartMonth || 2;
  const sem2EndDay = formData.semester2EndDay || 30;
  const sem2EndMonth = formData.semester2EndMonth || 6;

  // Debug: log the comparison values
  console.log('Validation check:', {
    academicStart: `${startDay}/${startMonth}`,
    academicEnd: `${endDay}/${endMonth}`,
    sem1Start: `${sem1StartDay}/${sem1StartMonth}`,
    sem1End: `${sem1EndDay}/${sem1EndMonth}`,
    sem2Start: `${sem2StartDay}/${sem2StartMonth}`,
    sem2End: `${sem2EndDay}/${sem2EndMonth}`,
  });

  // Semester 1 Start must be >= academic year start
  const sem1StartVsAcademicStart = compareDatesInAcademicYear(sem1StartDay, sem1StartMonth, startDay, startMonth, startMonth);
  console.log('sem1Start vs academicStart:', sem1StartVsAcademicStart);
  if (sem1StartVsAcademicStart < 0) {
    errors.push('La date de début du semestre 1 doit être en même temps ou après la date de début de l\'année académique');
  }

  // Semester 1 End must be <= academic year end
  // Pass true for isDate2EndOfYear since we're comparing against academic year end
  const sem1EndVsAcademicEnd = compareDatesInAcademicYear(sem1EndDay, sem1EndMonth, endDay, endMonth, startMonth, true);
  console.log('sem1End vs academicEnd:', sem1EndVsAcademicEnd);
  if (sem1EndVsAcademicEnd > 0) {
    errors.push('La date de fin du semestre 1 doit être en même temps ou avant la date de fin de l\'année académique');
  }

  // Semester 1 End must be > Semester 1 Start
  const sem1EndVsSem1Start = compareDatesInAcademicYear(sem1EndDay, sem1EndMonth, sem1StartDay, sem1StartMonth, startMonth);
  console.log('sem1End vs sem1Start:', sem1EndVsSem1Start);
  if (sem1EndVsSem1Start <= 0) {
    errors.push('La date de fin du semestre 1 doit être après la date de début du semestre 1');
  }

  // Semester 2 Start must be >= academic year start
  const sem2StartVsAcademicStart = compareDatesInAcademicYear(sem2StartDay, sem2StartMonth, startDay, startMonth, startMonth);
  console.log('sem2Start vs academicStart:', sem2StartVsAcademicStart);
  if (sem2StartVsAcademicStart < 0) {
    errors.push('La date de début du semestre 2 doit être en même temps ou après la date de début de l\'année académique');
  }

  // Semester 2 End must be <= academic year end
  // Pass true for isDate2EndOfYear since we're comparing against academic year end
  const sem2EndVsAcademicEnd = compareDatesInAcademicYear(sem2EndDay, sem2EndMonth, endDay, endMonth, startMonth, true);
  console.log('sem2End vs academicEnd:', sem2EndVsAcademicEnd);
  if (sem2EndVsAcademicEnd > 0) {
    errors.push('La date de fin du semestre 2 doit être en même temps ou avant la date de fin de l\'année académique');
  }

  // Semester 2 End must be > Semester 2 Start
  const sem2EndVsSem2Start = compareDatesInAcademicYear(sem2EndDay, sem2EndMonth, sem2StartDay, sem2StartMonth, startMonth);
  console.log('sem2End vs sem2Start:', sem2EndVsSem2Start);
  if (sem2EndVsSem2Start <= 0) {
    errors.push('La date de fin du semestre 2 doit être après la date de début du semestre 2');
  }

  // Semester 2 Start must be > Semester 1 End
  const sem2StartVsSem1End = compareDatesInAcademicYear(sem2StartDay, sem2StartMonth, sem1EndDay, sem1EndMonth, startMonth);
  console.log('sem2Start vs sem1End:', sem2StartVsSem1End);
  if (sem2StartVsSem1End <= 0) {
    errors.push('La date de début du semestre 2 doit être après la date de fin du semestre 1');
  }

  console.log('Total errors:', errors.length, errors);
  return errors;
};

// Check if form data has changed from original
const checkHasChanges = (formData: CreateAcademicYearTemplateDto, originalData: CreateAcademicYearTemplateDto | null): boolean => {
  if (!originalData) return true;
  return (
    formData.startDay !== originalData.startDay ||
    formData.startMonth !== originalData.startMonth ||
    formData.semester1StartDay !== originalData.semester1StartDay ||
    formData.semester1StartMonth !== originalData.semester1StartMonth ||
    formData.semester1EndDay !== originalData.semester1EndDay ||
    formData.semester1EndMonth !== originalData.semester1EndMonth ||
    formData.semester2StartDay !== originalData.semester2StartDay ||
    formData.semester2StartMonth !== originalData.semester2StartMonth ||
    formData.semester2EndDay !== originalData.semester2EndDay ||
    formData.semester2EndMonth !== originalData.semester2EndMonth
  );
};

const DEFAULT_FORM_DATA: CreateAcademicYearTemplateDto = {
  startDay: 1,
  startMonth: 9,
  endDay: 31,
  endMonth: 8,
  semester1StartDay: 1,
  semester1StartMonth: 9,
  semester1EndDay: 31,
  semester1EndMonth: 1,
  semester2StartDay: 1,
  semester2StartMonth: 2,
  semester2EndDay: 30,
  semester2EndMonth: 6,
};

interface AcademicYearTemplateFormProps {
  canManage?: boolean;
}

export const AcademicYearTemplateForm: React.FC<AcademicYearTemplateFormProps> = ({ canManage = true }) => {
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState<CreateAcademicYearTemplateDto | null>(null);
  const [formData, setFormData] = useState<CreateAcademicYearTemplateDto>(DEFAULT_FORM_DATA);

  // Campus management
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [hasMultipleCampuses, setHasMultipleCampuses] = useState(false);
  const [configMode, setConfigMode] = useState<'common' | 'per-campus'>('common');
  const [selectedCampusId, setSelectedCampusId] = useState<string | null>(null);

  // Computed values using useMemo to ensure reactivity
  const validationErrors = useMemo(() => validateFormData(formData), [formData]);
  const hasChanges = useMemo(() => checkHasChanges(formData, originalData), [formData, originalData]);

  // Button should be disabled when: saving OR no changes OR has validation errors
  const isButtonDisabled = saving || !hasChanges || validationErrors.length > 0;

  // Debug logging
  console.log('Form state:', {
    templateId,
    formData,
    originalData,
    hasChanges,
    validationErrors,
    isButtonDisabled,
    saving
  });

  useEffect(() => {
    loadCampuses();
  }, []);

  useEffect(() => {
    loadTemplate();
  }, [selectedCampusId, configMode]);

  const loadCampuses = async () => {
    try {
      const token = localStorage.getItem('nexedu_access_token');
      if (!token) return;

      const campusList = await fetchCampuses(token);
      setCampuses(campusList);
      setHasMultipleCampuses(campusList.length > 1);
    } catch (err) {
      console.error('Failed to load campuses:', err);
    }
  };

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const campusId = configMode === 'per-campus' ? selectedCampusId || undefined : undefined;
      const data = await calendarSettingsService.getAcademicYearTemplate(campusId);

      if (data) {
        setTemplateId(data.id); // Store the template ID for updates
        const loadedData: CreateAcademicYearTemplateDto = {
          campusId: data.campusId,
          startDay: data.startDay ?? DEFAULT_FORM_DATA.startDay,
          startMonth: data.startMonth ?? DEFAULT_FORM_DATA.startMonth,
          endDay: data.endDay ?? DEFAULT_FORM_DATA.endDay,
          endMonth: data.endMonth ?? DEFAULT_FORM_DATA.endMonth,
          semester1StartDay: data.semester1StartDay ?? DEFAULT_FORM_DATA.semester1StartDay,
          semester1StartMonth: data.semester1StartMonth ?? DEFAULT_FORM_DATA.semester1StartMonth,
          semester1EndDay: data.semester1EndDay ?? DEFAULT_FORM_DATA.semester1EndDay,
          semester1EndMonth: data.semester1EndMonth ?? DEFAULT_FORM_DATA.semester1EndMonth,
          semester2StartDay: data.semester2StartDay ?? DEFAULT_FORM_DATA.semester2StartDay,
          semester2StartMonth: data.semester2StartMonth ?? DEFAULT_FORM_DATA.semester2StartMonth,
          semester2EndDay: data.semester2EndDay ?? DEFAULT_FORM_DATA.semester2EndDay,
          semester2EndMonth: data.semester2EndMonth ?? DEFAULT_FORM_DATA.semester2EndMonth,
        };
        setFormData(loadedData);
        setOriginalData(loadedData);
      } else {
        setTemplateId(null); // No existing template
        const defaultData: CreateAcademicYearTemplateDto = {
          ...DEFAULT_FORM_DATA,
          campusId: configMode === 'per-campus' ? selectedCampusId || undefined : undefined,
        };
        setFormData(defaultData);
        setOriginalData(defaultData);
      }
    } catch (err) {
      console.error('Failed to load academic year template:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEditing = () => {
    setOriginalData({ ...formData });
    setIsEditing(true);
  };

  const handleCancelEditing = () => {
    if (originalData) {
      setFormData(originalData);
    }
    setIsEditing(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Don't submit if disabled
    if (isButtonDisabled) return;

    setError(null);
    setSuccess(false);

    try {
      setSaving(true);
      const calculatedEnd = calculateEndDate(formData.startDay || 1, formData.startMonth || 9);
      const dataToSave: CreateAcademicYearTemplateDto = {
        ...formData,
        endDay: calculatedEnd.endDay,
        endMonth: calculatedEnd.endMonth,
        campusId: configMode === 'per-campus' ? selectedCampusId || undefined : undefined,
      };
      // Pass templateId to use PATCH for updates, POST for new
      const result = await calendarSettingsService.createOrUpdateAcademicYearTemplate(dataToSave, templateId || undefined);
      setTemplateId(result.id); // Update templateId in case it was a new creation
      setOriginalData({ ...formData });
      setIsEditing(false);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { message?: string } } };
        setError(axiosError.response?.data?.message || 'Une erreur est survenue');
      } else if (err instanceof Error) {
        setError(err.message || 'Une erreur est survenue');
      } else {
        setError('Une erreur est survenue');
      }
    } finally {
      setSaving(false);
    }
  };

  const getDaysInMonth = (month: number) => {
    return DAYS_PER_MONTH[month - 1] || 31;
  };

  const updateFormField = (field: keyof CreateAcademicYearTemplateDto, value: number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Header with Edit/Save Button - only shown if user can manage */}
      {canManage && (
        <div className="flex justify-end">
          {!isEditing ? (
            <button
              type="button"
              onClick={handleStartEditing}
              className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              <Pencil size={20} />
              Modifier
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleCancelEditing}
                className="flex items-center gap-2 px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={isButtonDisabled}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg transition-colors ${
                  isButtonDisabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                }`}
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save size={20} />
                )}
                Enregistrer
              </button>
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          <Check size={20} />
          <span>Modèle enregistré avec succès</span>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <GraduationCap className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h4 className="font-medium text-blue-900">Modèle d'année académique</h4>
            <p className="text-sm text-blue-700 mt-1">
              Configurez le modèle de dates (jour/mois) pour l'année académique.
              Ce modèle sera utilisé pour créer automatiquement les années académiques.
            </p>
          </div>
        </div>
      </div>

      {/* Campus Selection (only show if multiple campuses) */}
      {hasMultipleCampuses && (
        <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building2 className="text-purple-600" size={20} />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Configuration par campus</h3>
              <p className="text-sm text-gray-500">
                Choisissez si l'année académique est commune à tous les campus ou spécifique à chaque campus
              </p>
            </div>
          </div>

          <div className="flex gap-4 mb-4">
            <button
              type="button"
              onClick={() => {
                setConfigMode('common');
                setSelectedCampusId(null);
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                configMode === 'common'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              <Globe size={18} />
              Commune à tous
            </button>
            <button
              type="button"
              onClick={() => {
                setConfigMode('per-campus');
                if (campuses.length > 0 && !selectedCampusId) {
                  setSelectedCampusId(campuses[0].id);
                }
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                configMode === 'per-campus'
                  ? 'bg-purple-600 text-white border-purple-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
              }`}
            >
              <Building2 size={18} />
              Par campus
            </button>
          </div>

          {configMode === 'per-campus' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sélectionner le campus
              </label>
              <select
                value={selectedCampusId || ''}
                onChange={(e) => setSelectedCampusId(e.target.value)}
                className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
              >
                {campuses.map((campus) => (
                  <option key={campus.id} value={campus.id}>
                    {campus.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Academic Year Period */}
      <div className="bg-gray-50 rounded-xl p-6">
        <h3 className="font-semibold text-gray-900 mb-4">Période de l'année académique</h3>

        <div className="grid grid-cols-2 gap-6">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-900">
                <span className="font-medium">
                  {formData.startDay || 1} {MONTHS.find(m => m.value === (formData.startMonth || 9))?.label}
                </span>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={formData.startDay || 1}
                  onChange={(e) => updateFormField('startDay', parseInt(e.target.value))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {Array.from({ length: getDaysInMonth(formData.startMonth || 9) }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <select
                  value={formData.startMonth || 9}
                  onChange={(e) => updateFormField('startMonth', parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>{month.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* End Date (Auto-calculated, read-only display) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            {(() => {
              const { endDay, endMonth } = calculateEndDate(formData.startDay || 1, formData.startMonth || 9);
              const endMonthLabel = MONTHS.find(m => m.value === endMonth)?.label || '';
              return (
                <>
                  <div className="px-4 py-2 bg-gray-100 border border-gray-200 rounded-lg text-gray-700">
                    <span className="font-medium">{endDay} {endMonthLabel}</span>
                    <span className="text-gray-500 ml-2">(année suivante)</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Info size={12} />
                    La date de fin est automatiquement calculée (dernier jour du mois précédant le début)
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Semester 1 */}
      <div className="bg-green-50 rounded-xl p-6 border border-green-200">
        <h3 className="font-semibold text-green-900 mb-4">Semestre 1</h3>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-white border border-green-200 rounded-lg text-gray-900">
                <span className="font-medium">
                  {formatDateDisplay(
                    formData.semester1StartDay || 1,
                    formData.semester1StartMonth || 9,
                    formData.startMonth || 9
                  )}
                </span>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={formData.semester1StartDay || 1}
                  onChange={(e) => updateFormField('semester1StartDay', parseInt(e.target.value))}
                  className="w-24 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                >
                  {Array.from({ length: getDaysInMonth(formData.semester1StartMonth || 9) }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <select
                  value={formData.semester1StartMonth || 9}
                  onChange={(e) => updateFormField('semester1StartMonth', parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {getMonthLabel(month.value, formData.startMonth || 9)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-white border border-green-200 rounded-lg text-gray-900">
                <span className="font-medium">
                  {formatDateDisplay(
                    formData.semester1EndDay || 31,
                    formData.semester1EndMonth || 1,
                    formData.startMonth || 9
                  )}
                </span>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={formData.semester1EndDay || 31}
                  onChange={(e) => updateFormField('semester1EndDay', parseInt(e.target.value))}
                  className="w-24 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                >
                  {Array.from({ length: getDaysInMonth(formData.semester1EndMonth || 1) }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <select
                  value={formData.semester1EndMonth || 1}
                  onChange={(e) => updateFormField('semester1EndMonth', parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 bg-white"
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {getMonthLabel(month.value, formData.startMonth || 9)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Semester 2 */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="font-semibold text-blue-900 mb-4">Semestre 2</h3>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-white border border-blue-200 rounded-lg text-gray-900">
                <span className="font-medium">
                  {formatDateDisplay(
                    formData.semester2StartDay || 1,
                    formData.semester2StartMonth || 2,
                    formData.startMonth || 9
                  )}
                </span>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={formData.semester2StartDay || 1}
                  onChange={(e) => updateFormField('semester2StartDay', parseInt(e.target.value))}
                  className="w-24 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {Array.from({ length: getDaysInMonth(formData.semester2StartMonth || 2) }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <select
                  value={formData.semester2StartMonth || 2}
                  onChange={(e) => updateFormField('semester2StartMonth', parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {getMonthLabel(month.value, formData.startMonth || 9)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            {!isEditing ? (
              <div className="px-4 py-2 bg-white border border-blue-200 rounded-lg text-gray-900">
                <span className="font-medium">
                  {formatDateDisplay(
                    formData.semester2EndDay || 30,
                    formData.semester2EndMonth || 6,
                    formData.startMonth || 9
                  )}
                </span>
              </div>
            ) : (
              <div className="flex gap-2">
                <select
                  value={formData.semester2EndDay || 30}
                  onChange={(e) => updateFormField('semester2EndDay', parseInt(e.target.value))}
                  className="w-24 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {Array.from({ length: getDaysInMonth(formData.semester2EndMonth || 6) }, (_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                  ))}
                </select>
                <select
                  value={formData.semester2EndMonth || 6}
                  onChange={(e) => updateFormField('semester2EndMonth', parseInt(e.target.value))}
                  className="flex-1 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  {MONTHS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {getMonthLabel(month.value, formData.startMonth || 9)}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Validation Warnings */}
      {validationErrors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h4 className="font-medium text-amber-900">Attention</h4>
              <ul className="text-sm text-amber-700 mt-1 list-disc list-inside">
                {validationErrors.map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </ul>
              {isEditing && (
                <p className="text-xs text-amber-600 mt-2 italic">
                  Veuillez corriger ces erreurs pour pouvoir enregistrer.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </form>
  );
};
